import { logger } from '@/core/logger';
import * as forge from 'node-forge';

const PUBLIC_KEY_ENV = 'EXPO_PUBLIC_RSA_PUBLIC_KEY';

function readPublicKeySpki(): string {
    const key = process.env.EXPO_PUBLIC_RSA_PUBLIC_KEY?.replace(/\s/g, '');
    if (!key) {
        throw new Error(`Missing ${PUBLIC_KEY_ENV}`);
    }
    return key;
}

function base64ToBytes(value: string): ArrayBuffer {
    const normalized = value.replace(/\s/g, '');
    const binary =
        typeof atob === 'function'
            ? atob(normalized)
            : Buffer.from(normalized, 'base64').toString('binary');
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.codePointAt(i) ?? 0;
    }
    return bytes.buffer;
}

function bytesToBase64(bytes: Uint8Array): string {
    let binary = '';
    for (const byte of bytes) {
        binary += String.fromCodePoint(byte);
    }
    return typeof btoa === 'function'
        ? btoa(binary)
        : Buffer.from(binary, 'binary').toString('base64');
}

function spkiToPem(spki: string): string {
    const body = spki.match(/.{1,64}/g)?.join('\n') ?? spki;
    return `-----BEGIN PUBLIC KEY-----\n${body}\n-----END PUBLIC KEY-----`;
}

async function encryptWithWebCrypto(value: string, publicKeySpki: string): Promise<string> {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) {
        throw new Error('Web Crypto is unavailable');
    }

    const key = await subtle.importKey(
        'spki',
        base64ToBytes(publicKeySpki),
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt'],
    );
    const cipher = await subtle.encrypt(
        { name: 'RSA-OAEP' },
        key,
        new TextEncoder().encode(value),
    );
    return bytesToBase64(new Uint8Array(cipher));
}

function encryptWithForge(value: string, publicKeySpki: string): string {
    const publicKey = forge.pki.publicKeyFromPem(spkiToPem(publicKeySpki));
    const encrypted = publicKey.encrypt(value, 'RSA-OAEP', {
        md: forge.md.sha256.create(),
        mgf1: {
            md: forge.md.sha256.create(),
        },
    });
    return forge.util.encode64(encrypted);
}

/**
 * RSA-OAEP SHA-256 ciphertext, standard base64.
 * Web uses Web Crypto. iOS and Android use the same padding through node-forge.
 */
export async function encryptRsaOaep(plaintext: string): Promise<string> {
    const value = plaintext.trim();
    if (!value) {
        throw new Error('Value to encrypt is required');
    }

    const publicKeySpki = readPublicKeySpki();

    if (globalThis.crypto?.subtle) {
        try {
            return await encryptWithWebCrypto(value, publicKeySpki);
        } catch (error) {
            logger.warn('rsaOaep:web-crypto-failed', { error: String(error) });
        }
    }

    try {
        return encryptWithForge(value, publicKeySpki);
    } catch (error) {
        logger.error('rsaOaep:encrypt-failed', { error: String(error) });
        throw new Error('Unable to encrypt value');
    }
}
